import { useId, useRef } from 'react';
import { clsx } from 'clsx';
import { Button } from './ui';
import { useModalBehaviour } from './use-modal';

/**
 * The dialog for a decision that cannot be taken back with one click.
 *
 * It replaces `window.confirm`, which the browser draws itself: a black box
 * with the ORIGIN as its heading, no room for the pod and room names, and no
 * way to show what is actually about to happen. On a screen whose whole job is
 * "this pod, that room", a confirmation that cannot name either of them is the
 * wrong shape — and reading "calendar-adapter.zenspace.io says" before the
 * question makes the product look like it is talking to itself.
 *
 * What it does that the native one cannot:
 *   - names the subject, and can lay out consequences as a list;
 *   - keeps the destructive action styled as destructive;
 *   - shows a busy state, so the request is visibly in flight rather than
 *     leaving a dead dialog behind while the row quietly changes;
 *   - is a real dialog for assistive tech (`role="dialog"`, `aria-modal`,
 *     labelled by its own heading).
 *
 * Keyboard behaviour — Escape, the Tab trap, restoring focus, locking the page
 * behind — lives in `useModalBehaviour`, shared with `Modal`. It is the part
 * that is easy to get wrong, and a second copy of it would drift.
 *
 * FOR A SHORT QUESTION ONLY. Anything tall enough to scroll belongs in `Modal`,
 * which keeps its heading and buttons in view while its body moves; this panel
 * grows with its content and would push its own buttons off the screen.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'destructive',
  busy = false,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean;
  title: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'destructive' | 'primary';
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const headingId = useId();

  // Cancel is FIRST IN THE DOM, which is what makes the shared hook's
  // "focus the first control" rule safe here: a stray Enter on a dialog that
  // has only just appeared must not be the thing that unlinks a pod.
  useModalBehaviour({ open, panel, onClose: () => !busy && onCancel() });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-[20px]"
      // Clicking away cancels — but only on the backdrop itself, so a drag that
      // ends outside the panel does not count as a decision.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onCancel();
      }}
    >
      <div className="absolute inset-0 bg-ink/45 backdrop-blur-[2px]" aria-hidden="true" />

      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        className={clsx(
          'relative w-full max-w-[460px] rounded-feature bg-card p-[28px] shadow-modal',
          'animate-rise',
        )}
      >
        <h2 id={headingId} className="m-0 text-subheading font-semibold">
          {title}
        </h2>

        {children && <div className="mt-[10px] text-body text-muted">{children}</div>}

        <div className="mt-[24px] flex flex-wrap justify-end gap-[10px]">
          <Button tone="quiet" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button tone={tone} busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
